/**
 * Launch gating — single source of truth for which long-tail / not-yet-ready
 * features are hidden from the public launch of qyngent.com (Plan 33, RR-5).
 *
 * Decided with the product owner: the SHIP surface is the acquisition+activation
 * funnel + the marketing engine. Everything else is flagged OFF here until it is
 * proven both fully wired AND launch-essential.
 *
 * This is a STATIC default-off registry consumed immediately by the sidebar nav
 * and the route guard. RR-5 will swap `isFeatureEnabled` to read tier-aware
 * entitlements (subscription tier + plan_matrix, served via /api/config/pricing)
 * and the backend FeatureFlagsService overrides — without changing call sites.
 *
 * Backing audit: reports/release-readiness/audit_result.json (synthesis.flag_off).
 */

import type { Tier } from '@/hooks/useEntitlements'

export type LaunchFlag =
  | 'agency_marketplace'
  | 'ai_memory_knowledge'
  | 'brain_companion_pages'
  | 'app_marketing'
  | 'autonomous_dev'
  | 'business_signup'
  | 'community_showcase'
  | 'marketing_providers'
  | 'compliance_audit'
  | 'content_calendar_plan'
  | 'developer_portal'
  | 'docs_portal'
  | 'hosted_app_observability'
  | 'model_internal_inspector'
  | 'observability_cost'
  | 'observability_ops'
  | 'onboarding_enhanced_quiz'
  | 'platform_admin'
  | 'public_status_page'
  | 'rbac_sso_enterprise'
  | 'retention_dashboard'
  | 'tenant_config'
  | 'webhook_config'

/** Default state of every launch flag. All OFF for the public launch. */
export const LAUNCH_FLAGS: Record<LaunchFlag, boolean> = {
  agency_marketplace: false,
  ai_memory_knowledge: false,
  // Brain's companion page (Knowledge Graph) — enabled for pro+ while the
  // rest of the ai_memory_knowledge bucket (/knowledge, /correction-learning) stays gated.
  brain_companion_pages: true,
  app_marketing: false,
  autonomous_dev: false,
  // Business ladder: RR-9 (team invitations, seats, roles) shipped 2026-09-06,
  // so the Business option is offered in onboarding and /pricing renders the
  // business plan ladder. Backing surfaces verified in prod: Stripe tax_id
  // collection (ABN) + AU GST, plan_matrix business limits, and seat enforcement
  // via plan_matrix max_seats.
  business_signup: true,
  community_showcase: false,
  marketing_providers: false,
  compliance_audit: false,
  // F-062: hidden 10-06 (E7 / Q-05 A) because the page called /calendar/*, which
  // the ingress never routes to the API. On since 2026-11-01: the API serves
  // /api/calendar/* with a project-access check on every route. Off again = hidden.
  content_calendar_plan: true,
  developer_portal: false,
  docs_portal: false,
  hosted_app_observability: false,
  model_internal_inspector: false,
  observability_cost: false,
  observability_ops: false,
  onboarding_enhanced_quiz: false,
  platform_admin: false,
  public_status_page: false,
  rbac_sso_enterprise: false,
  // E7 / Q-05 A: F-095. /retention calls /api/projects/{id}/retention/*, and
  // no route declares them. Hidden until a product fix serves them.
  retention_dashboard: false,
  tenant_config: false,
  webhook_config: false,
}

/**
 * Route path → launch flag. A path present here is hidden when its flag is OFF.
 * Paths NOT listed are part of the SHIP surface and always visible.
 * Note: platform_admin/compliance routes are additionally role-gated server-side;
 * this hides them from the nav + direct-URL for non-launch builds.
 */
export const PATH_FLAG: Record<string, LaunchFlag> = {
  '/admin/agents': 'platform_admin',
  '/admin/catalog': 'platform_admin',
  '/admin/ops': 'platform_admin',
  '/admin/providers': 'platform_admin',
  '/admin/system': 'platform_admin',
  '/agency': 'agency_marketplace',
  '/app-marketing': 'app_marketing',
  '/agent-coordination': 'ai_memory_knowledge',
  '/approvals': 'platform_admin',
  '/apps/:appId/logs': 'hosted_app_observability',
  '/apps/:appId/metrics': 'hosted_app_observability',
  '/audit-log': 'compliance_audit',
  '/autonomous-dev': 'autonomous_dev',
  '/backups': 'observability_ops',
  '/changelog': 'docs_portal',
  '/compliance': 'compliance_audit',
  '/content-studio/calendar': 'content_calendar_plan',
  '/content-studio/templates': 'agency_marketplace',
  '/context-budget': 'model_internal_inspector',
  '/correction-learning': 'ai_memory_knowledge',
  '/costs': 'observability_cost',
  '/developers': 'developer_portal',
  '/developers/earnings': 'developer_portal',
  '/error-prediction': 'ai_memory_knowledge',
  '/help': 'docs_portal',
  '/knowledge': 'ai_memory_knowledge',
  '/knowledge-graph': 'brain_companion_pages',
  '/marketing/config': 'tenant_config',
  '/marketing/providers': 'marketing_providers',
  '/marketing/supply-demand': 'agency_marketplace',
  '/marketing/tasks': 'observability_ops',
  '/marketing/webhooks': 'webhook_config',
  '/marketplace': 'agency_marketplace',
  // Plan 51: the Memory Inspector is ungated (read-only, tenant-scoped view of the
  // workspace's own memory) now that the Brain is live in prod. The other
  // ai_memory_knowledge pages (/knowledge, /agent-memory, …) stay gated.
  '/monitoring': 'observability_ops',
  '/rbac-settings': 'rbac_sso_enterprise',
  '/retention': 'retention_dashboard',
  '/rules': 'tenant_config',
  '/self-healing': 'observability_ops',
  '/showcase': 'community_showcase',
  '/sla': 'observability_ops',
  '/sso-settings': 'rbac_sso_enterprise',
  '/status': 'public_status_page',
  '/token-usage': 'observability_cost',
  '/traces': 'observability_ops',
  '/widget-config': 'developer_portal',
}

// ───────────────────────── Tier entitlements (RR-5) ─────────────────────────
//
// A feature is usable only when it is BOTH "shipped" (static launch flag on OR a
// backend per-workspace override on) AND the user's plan tier permits it. The
// pure functions below read a module-level snapshot that <LaunchEntitlementsSync>
// (App.tsx) keeps updated from useEntitlements() + the backend resolved flags —
// so the ~6 call sites (Sidebar isPathEnabled, route guard isUrlEnabled) stay
// non-React and unchanged.

const TIER_RANK: Record<Tier, number> = {
  anonymous: 0, free: 1, pro: 2, max: 3, ultra: 4, team: 5, enterprise: 6,
}

/**
 * Minimum subscription tier required for each launch feature ONCE it ships.
 * Flags absent here have no tier requirement (any authed user, if shipped).
 * This is the product map — tune freely; it only bites after a flag is flipped on.
 * `platform_admin` is role-gated (admin bypass below), not tier-gated.
 */
export const FLAG_MIN_TIER: Partial<Record<LaunchFlag, Tier>> = {
  rbac_sso_enterprise: 'enterprise',
  compliance_audit: 'enterprise',
  model_internal_inspector: 'enterprise',
  observability_cost: 'pro',
  observability_ops: 'pro',
  hosted_app_observability: 'pro',
  webhook_config: 'pro',
  developer_portal: 'pro',
  agency_marketplace: 'pro',
  // business_signup is deliberately ABSENT from this map. It gates the *signup
  // path*, whose whole audience is pre-purchase — an anonymous visitor on
  // /pricing, or a free-tier user in onboarding. A minimum tier here made it
  // unreachable by exactly those people (you had to already hold the team plan
  // to be shown how to buy it). Do not re-add it; see launchGating.test.ts.
  app_marketing: 'pro',
  ai_memory_knowledge: 'pro',
  brain_companion_pages: 'pro',
  autonomous_dev: 'pro',
}

// Module snapshot — pushed by setLaunchEntitlements/setBackendFlagOverrides.
let _tier: Tier = 'anonymous'
let _isPlatformAdmin = false
let _backendFlags: Record<string, boolean> = {}

/** Push the current user's tier + platform-admin into the gate (from React). */
export function setLaunchEntitlements(snap: { tier: Tier; isPlatformAdmin: boolean }): void {
  _tier = snap.tier
  _isPlatformAdmin = snap.isPlatformAdmin
}

/** Push the backend FeatureFlagsService resolved overrides ({flagKey: on}). */
export function setBackendFlagOverrides(flags: Record<string, boolean>): void {
  _backendFlags = flags || {}
}

/** True when the current tier meets a flag's minimum-tier requirement.
 *  `tier` overrides the module snapshot — pass the live `useEntitlements().tier` from a React
 *  component (e.g. the route guard) so evaluation doesn't depend on the effect-synced snapshot. */
export function tierAllows(flag: LaunchFlag, tier?: Tier): boolean {
  const min = FLAG_MIN_TIER[flag]
  if (!min) return true
  return TIER_RANK[tier ?? _tier] >= TIER_RANK[min]
}

export type GateResult = 'allowed' | 'not_shipped' | 'needs_upgrade'

/**
 * Full evaluation for a single flag, distinguishing the two "off" reasons so the
 * UI can show an upgrade prompt (needs_upgrade) vs. simply hide it (not_shipped).
 */
export function evaluateFlag(flag: LaunchFlag, tier?: Tier, isAdmin?: boolean): GateResult {
  // `platform_admin` guards nothing by itself: the admin pages sit behind
  // ProtectedAdminRoute and every admin API call behind require_admin. So a
  // platform admin is always let through it, whatever the launch flag says.
  // Without this, LaunchGate bounced admins off /admin/* to /dashboard while
  // ProtectedRoute bounced them back to /admin/providers: an endless redirect
  // loop and a blank page. Non-admins still see the routes hidden.
  if (flag === 'platform_admin' && (isAdmin ?? _isPlatformAdmin)) return 'allowed'
  const shipped = LAUNCH_FLAGS[flag] === true || _backendFlags[flag] === true
  if (!shipped) return 'not_shipped'
  if (isAdmin ?? _isPlatformAdmin) return 'allowed' // operators bypass the tier gate
  return tierAllows(flag, tier) ? 'allowed' : 'needs_upgrade'
}

/** The tier a flag requires, or null when it has no tier requirement. */
export function requiredTier(flag: LaunchFlag): Tier | null {
  return FLAG_MIN_TIER[flag] ?? null
}

/**
 * True if a flag is fully usable now (shipped AND tier permits). Back-compat name
 * kept so call sites (isPathEnabled/isUrlEnabled) don't change.
 */
export function isFeatureEnabled(flag: LaunchFlag): boolean {
  return evaluateFlag(flag) === 'allowed'
}

/**
 * UI-block flags (RR-4) — hide individual UI controls (not whole routes) whose
 * backend is broken or stubbed at launch:
 *  - github_oauth: GitHub OAuth start/callback is broken end-to-end.
 *  - publish_dispatch: marketing "publish" is a DB-status stub that never
 *    dispatches to a connector, so the button must not promise a publish.
 *
 * E7 / Q-05 A (owner 2026-09-28, log G10): each flag in the second block hides
 * the one dead control in a feature whose UI calls a route, socket or link that
 * nothing serves (docs/discovery/features.md#at-a-glance). The API routes stay
 * mounted. A product fix that gives a control its missing handler flips its
 * flag to true (or deletes it) in the same release. Tests:
 * launchGating.hidden.test.ts.
 */
export type UiFlag =
  | 'github_oauth'
  | 'password_management'
  | 'publish_dispatch'
  | 'visual_editor_collab'
  | 'build_revert'
  | 'marketing_weekly_insights'
  | 'marketing_content_link'
  | 'video_script_generate'
  | 'ai_music_generation'
  | 'media_studio_generate'
  | 'referral_share_link'
  | 'preview_share_link'

export const UI_FLAGS: Record<UiFlag, boolean> = {
  github_oauth: false,
  // Re-enabled: reset/change-password now write to qyngent.users.password_hash
  // (the store sign_in verifies); the OTP-email forgot-password flow works E2E.
  password_management: true,
  publish_dispatch: false,
  // ── E7 / Q-05 A hides ──
  // F-037: the collaboration socket targets ws://localhost:8100/ws/collab with no
  // token; the mounted /ws/collab/{project_id} requires ?token= and closes.
  visual_editor_collab: false,
  // F-039: Revert posts /api/build-history/{id}/rollback; that route was deleted.
  build_revert: false,
  // F-053: WeeklyInsightsCard reads {overview:{recommendations}}; the route
  // returns flat counts, so the card can never show an insight.
  marketing_weekly_insights: false,
  // F-053: /marketing/content has no route in App.tsx (lands on NotFound).
  marketing_content_link: false,
  // F-068: POST /api/video/script/generate: no route declares it.
  video_script_generate: false,
  // F-068: /api/media/audio/{models,generate}: no route declares them.
  ai_music_generation: false,
  // F-077: POST /api/projects/{id}/media/generate: no route declares it.
  media_studio_generate: false,
  // F-089: the share link /ref/<code> has no route in App.tsx.
  referral_share_link: false,
  // F-090: the share link /preview/<slug> has no route in App.tsx.
  preview_share_link: false,
}

/** True if a UI-block flag is on. */
export function isUiEnabled(flag: UiFlag): boolean {
  return UI_FLAGS[flag] === true
}

/**
 * True if a route path is visible at launch. Unlisted paths (the SHIP surface)
 * are always enabled; listed paths follow their flag.
 *
 * Exact-match only — use this for static nav items (the Sidebar). For a concrete
 * URL that may contain resolved route params (the route guard), use isUrlEnabled.
 */
export function isPathEnabled(path: string): boolean {
  const flag = PATH_FLAG[path]
  return flag ? isFeatureEnabled(flag) : true
}

/** Split a path into non-empty segments (tolerates leading/trailing slashes). */
function segments(path: string): string[] {
  return path.split('/').filter(Boolean)
}

/**
 * Route-guard counterpart to isPathEnabled: match a CONCRETE URL pathname
 * (params already resolved, e.g. `/apps/abc-123/logs`) against the PATH_FLAG
 * patterns (which may contain `:param` placeholders). A `:param` segment matches
 * any single non-empty segment. Unlisted paths are SHIP surface → always enabled.
 *
 * Matching is segment-exact (no prefix leakage): `/marketing` does not match the
 * `/marketing/config` pattern and vice-versa.
 */
export function isUrlEnabled(pathname: string): boolean {
  return evaluateUrl(pathname).result === 'allowed'
}

/**
 * Route-guard evaluator with the upgrade/hide distinction. Returns the matched
 * flag + the tier it needs so the guard can render an upgrade prompt for
 * `needs_upgrade` (vs. silently hiding `not_shipped`). Unlisted paths (SHIP
 * surface) → allowed.
 */
export function evaluateUrl(
  pathname: string,
  tier?: Tier,
  isAdmin?: boolean,
): { result: GateResult; flag: LaunchFlag | null; tier: Tier | null } {
  const urlSegs = segments(pathname)
  for (const [pattern, flag] of Object.entries(PATH_FLAG)) {
    const patSegs = segments(pattern)
    if (patSegs.length !== urlSegs.length) continue
    const matches = patSegs.every(
      (seg, i) => seg.startsWith(':') || seg === urlSegs[i],
    )
    if (matches) return { result: evaluateFlag(flag, tier, isAdmin), flag, tier: requiredTier(flag) }
  }
  return { result: 'allowed', flag: null, tier: null }
}
